import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import {
  VentaAnimalesItem,
  VentaAnimalesService,
} from "../../../../generated/grpc/pesaje_ganaderia_pb";
import { grpcVentaAnimalesDatabase } from "../database/GrpcVentaAnimalesDatabase";
import { serviceSync } from "../../../../base/services/ServiceSync";
import {
  fx_nombreLote,
  fx_nombresLotesAsync,
  toLoteResumen,
} from "../../../terceros/services/LoteResumenUtils";
import { VentaAnimalesResponse } from "../response/VentaAnimalesResponse";

/** Variante gRPC-Web (con delta real, por finca) de `VentaAnimalesGetListByFincaIdService`
 *  (ver ese archivo). Devuelve `VentaAnimalesResponse[]` -mismo shape que ya
 *  usa el resto de la app-. Rellena `clienteId` (sin uso real) y arma
 *  `loteSalida` como `ClienteFincaLoteResponse` de relleno (`toLoteResumen`,
 *  mismo criterio que `CompraAnimalesGrpcService`). */
class VentaAnimalesGrpcService {
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<VentaAnimalesResponse[]> {
    await this._syncAsync();
    const items = await grpcVentaAnimalesDatabase.getListByFincaIdAsync<VentaAnimalesItem>(
      this._fincaId,
    );
    // Nombre actual de cada potrero: la venta guarda el que tenía al sincronizarse.
    const nombresLotes = await fx_nombresLotesAsync(this._fincaId);
    return items.map((item) => toVentaAnimalesResponse(item, nombresLotes));
  }

  clearCache(): void {
    serviceSync.reset(`grpc:ventas:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:ventas:${this._fincaId}`, async () => {
      const ultimaSincronizacion = await grpcVentaAnimalesDatabase.getUltimaSincronizacionAsync(
        this._fincaId,
      );
      const client = createClient(VentaAnimalesService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcVentaAnimalesDatabase.putDeltaAsync(
        this._fincaId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createVentaAnimalesGetListByFincaIdService`). */
export const fx_createVentaAnimalesGrpcService = createCachedFactory(
  (fincaId: string) => new VentaAnimalesGrpcService(fincaId),
);

/** Adapta un `VentaAnimalesItem` (gRPC) al shape `VentaAnimalesResponse`
 *  (REST). Exportada porque los servicios de guardado/borrado también la
 *  necesitan.
 *  @param nombresLotes Nombres actuales de los lotes (`fx_nombresLotesAsync`);
 *  sin él se usa el que trae la venta. */
export function toVentaAnimalesResponse(
  venta: VentaAnimalesItem,
  nombresLotes?: ReadonlyMap<string, string>,
): VentaAnimalesResponse {
  return {
    id: venta.id,
    isEliminado: venta.eliminado,
    clienteId: "",
    fincaId: venta.fincaId,
    loteSalida: toLoteResumen(
      venta.loteSalidaId,
      fx_nombreLote(nombresLotes, venta.loteSalidaId, venta.loteSalidaNombre),
    ),
    codigo: venta.codigo,
    fechaVenta: venta.fechaVenta,
    cantidadAnimales: venta.cantidadAnimales,
    pesoMinimo: venta.pesoMinimo,
    pesoMaximo: venta.pesoMaximo,
    pesoPromedio: venta.pesoPromedio,
    pesoTotal: venta.pesoTotal,
    pesosAnimales: venta.pesosAnimales,
    precioKilo: venta.precioKilo,
    precioTotal: venta.precioTotal,
  };
}
